import { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  includedFeatures,
  pricingTiers,
  packageSpecs,
  dueDiligenceBenefits,
} from "../mocks/protectionPackages";

export default function ProtectionPackages() {
  useEffect(() => {
    const originalTitle = document.title;
    document.title =
      "Your Protection Packages – Patient Safety & Due Diligence Tools | Dental Safety Abroad";
    const metaDesc = document.querySelector('meta[name="description"]');
    const originalDesc = metaDesc?.getAttribute("content") || "";
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Patient safety and due-diligence tools to help you make informed decisions about dental treatment abroad. Checklists, cost calculators, and specialist verification guides."
      );
    }
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute("content", window.location.href);
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle)
      ogTitle.setAttribute(
        "content",
        "Your Protection Packages – Dental Safety Abroad"
      );
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc)
      ogDesc.setAttribute(
        "content",
        "Patient safety and due-diligence tools to help you make informed decisions about dental treatment abroad."
      );
    return () => {
      document.title = originalTitle;
      if (metaDesc) metaDesc.setAttribute("content", originalDesc);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      {/* Schema.org structured data */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Safe Dental Tourism Protection System",
          description:
            "Patient safety and due-diligence tools for dental treatment abroad.",
          brand: {
            "@type": "Brand",
            name: "Dental Safety Abroad",
          },
          offers: pricingTiers.map((tier) => ({
            "@type": "Offer",
            name: tier.name,
            price: tier.price.replace(/[^0-9.]/g, ""),
            priceCurrency: "GBP",
            availability: "https://schema.org/InStock",
            url: tier.ctaUrl,
          })),
        })}
      </script>

      {/* Hero */}
      <section className="relative bg-[#182940] pt-24 md:pt-28">
        <div className="w-full px-4 md:px-6 lg:px-8 py-10 md:py-14 lg:py-16">
          <div className="max-w-4xl mx-auto text-center">
            <nav className="flex items-center justify-center gap-2 text-xs text-white/50 mb-6" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-white/80 transition-colors">
                Home
              </Link>
              <span className="w-3 h-3 flex items-center justify-center">
                <i className="ri-arrow-right-s-line"></i>
              </span>
              <span className="text-white/70" aria-current="page">
                Your Protection Packages
              </span>
            </nav>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white leading-tight mb-4">
              Protect Yourself Before You Commit to Treatment Abroad
            </h1>
            <p className="text-sm md:text-base text-white/60 leading-relaxed max-w-2xl mx-auto">
              Choosing dental treatment overseas can save thousands of pounds — but choosing the wrong clinic or dentist can lead to serious complications, poor outcomes, hidden costs, and expensive corrective treatment later.
            </p>
          </div>
        </div>
      </section>

      {/* Intro */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-3xl mx-auto text-center">
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
            That is why we created the{" "}
            <strong className="text-foreground-900">
              Safe Dental Tourism Protection System
            </strong>{" "}
            — a complete patient safety and due diligence toolkit designed to help you make informed, evidence-based decisions before booking treatment abroad.
          </p>
        </div>
      </section>

      {/* Due Diligence */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100/70">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              Due Diligence
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight">
              Make Safer, Smarter Decisions About Dental Treatment Abroad
            </h2>
          </div>
          <div className="bg-background-50 rounded-lg p-6 md:p-8">
            <ul className="space-y-3">
              {dueDiligenceBenefits.map((item, i) => (
                <li
                  key={i}
                  className="flex items-start gap-3 text-sm md:text-base text-foreground-700"
                >
                  <span className="w-5 h-5 flex items-center justify-center text-primary-600 mt-0.5 shrink-0">
                    <i className="ri-checkbox-circle-line text-lg"></i>
                  </span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* What's Included */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              The Complete Protection System
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-3">
              What is Included
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              A comprehensive toolkit engineered to provide complete transactional transparency.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {includedFeatures.map((feature, i) => (
              <div
                key={i}
                className="bg-background-50 rounded-lg p-5 md:p-6 border border-background-200/70"
              >
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-primary-100 text-primary-600 mb-4">
                  <i className={`${feature.icon} text-xl`}></i>
                </div>
                <h4 className="text-sm font-semibold text-foreground-900 mb-2">
                  {feature.title}
                </h4>
                <p className="text-xs md:text-sm text-foreground-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section id="packages" className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-background-100/70">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              Pricing Tiers
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-3">
              Choose Your Package
            </h2>
            <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
              Select the degree of protective oversight required for your planned clinical journey.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative bg-background-50 rounded-lg p-5 md:p-6 border ${
                  tier.badge
                    ? "border-primary-400"
                    : "border-background-200/70"
                } flex flex-col`}
              >
                {tier.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="inline-block px-3 py-1 text-xs font-semibold rounded-full bg-primary-500 text-white whitespace-nowrap">
                      {tier.badge}
                    </span>
                  </div>
                )}
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-foreground-900 mb-1">
                    {tier.name}
                  </h4>
                  <div className="flex items-baseline gap-1.5 mb-3">
                    <span className="text-2xl md:text-3xl font-bold text-primary-600">
                      {tier.price}
                    </span>
                    <span className="text-xs text-foreground-500">
                      / {tier.period}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm text-foreground-600 leading-relaxed mb-4">
                    {tier.description}
                  </p>
                  <ul className="space-y-2 mb-6">
                    {tier.features.map((f, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-xs md:text-sm text-foreground-700"
                      >
                        <span className="w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
                          <i className="ri-check-line"></i>
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <a
                  href={tier.ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-full px-4 py-2.5 text-sm font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  {tier.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* In-Depth Analysis */}
      <section id="details" className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
              In-Depth Analysis
            </span>
            <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight">
              Package Specifications
            </h2>
          </div>

          <div className="space-y-12 md:space-y-16">
            {packageSpecs.map((spec) => (
              <article key={spec.id} className="scroll-mt-28">
                <h3 className="text-lg md:text-xl font-bold text-foreground-900 mb-3">
                  {spec.title}
                </h3>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                  {spec.subtitle}
                </p>

                <h4 className="text-sm font-semibold text-primary-700 mb-3">
                  What It Includes
                </h4>
                <ul className="space-y-2 mb-6">
                  {spec.whatItIncludes.map((item, i) => (
                    <li
                      key={i}
                      className="flex items-start gap-2 text-sm text-foreground-700"
                    >
                      <span className="w-4 h-4 flex items-center justify-center text-primary-500 mt-0.5 shrink-0">
                        <i className="ri-check-line"></i>
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="bg-amber-50/70 border border-amber-200/60 rounded-lg p-4 md:p-5 mb-6">
                  <p className="text-sm text-foreground-700 leading-relaxed">
                    <strong className="text-foreground-900 flex items-center gap-1.5 mb-1">
                      <span className="w-4 h-4 flex items-center justify-center text-amber-600">
                        <i className="ri-alert-line"></i>
                      </span>
                      Operational Note:
                    </strong>
                    {spec.disclaimer}
                  </p>
                </div>

                <h4 className="text-sm font-semibold text-primary-700 mb-3">
                  How This Helps You
                </h4>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-2">
                  {spec.howItHelps}
                </p>
                <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                  {spec.howItHelpsDetail}
                </p>

                {spec.id === "complete" && (
                  <>
                    <h4 className="text-sm font-semibold text-primary-700 mb-3 mt-6">
                      Why Continuity of Care Matters
                    </h4>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-2 italic">
                      &ldquo;Will the same dentist who assessed me actually perform my treatment?&rdquo;
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-2">
                      Some clinics use senior dentists during consultations and marketing activities but allocate treatment to different clinicians later. The dentist who develops your treatment plan should ideally remain involved throughout the process. Continuity of care reduces communication errors, improves treatment consistency and often results in more predictable outcomes.
                    </p>
                    <p className="text-sm md:text-base text-foreground-600 leading-relaxed">
                      This is particularly important for implants, All-on-4 treatment, full-mouth rehabilitation and complex restorative cases.
                    </p>
                  </>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Long-Term View CTA */}
      <section className="w-full px-4 md:px-6 lg:px-8 py-12 md:py-16 bg-accent-100/60">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-xs font-semibold uppercase tracking-widest text-primary-600 mb-2 block">
            Long-Term View
          </span>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground-950 leading-tight mb-4">
            Your Dental Health Is a Long-Term Investment
          </h2>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
            The cheapest option is not always the safest option. Taking the time to properly vet a clinic, understand qualifications, and compare the true cost of treatment can help you avoid unnecessary risks, future corrective work, and costly mistakes.
          </p>
          <h4 className="text-sm font-semibold text-foreground-900 mb-2">
            Choose Safely. Choose Confidently.
          </h4>
          <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-8">
            Whether you are considering implants, veneers, crowns, All-on-4 treatment, or full-mouth rehabilitation abroad, our protection systems help you approach dental tourism safely and intelligently.{" "}
            <strong className="text-foreground-900">
              Protect your smile for the long term.
            </strong>
          </p>
          <Link
            to="/quiz"
            className="inline-flex items-center justify-center px-6 py-3 text-base font-medium rounded-md bg-primary-500 text-white hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            Take the Free Quiz
            <span className="w-5 h-5 flex items-center justify-center ml-2">
              <i className="ri-arrow-right-line"></i>
            </span>
          </Link>
        </div>
      </section>
    </div>
  );
}